"use client"

import type { ComponentProps, ReactNode } from "react"

import { Canvas, extend } from "@react-three/fiber"
import type { ThreeToJSXElements } from "@react-three/fiber"
import * as THREE from "three/webgpu"

extend(THREE as any)

type CanvasProps = ComponentProps<typeof Canvas>

interface WebGPUCanvasProps extends Omit<CanvasProps, "gl"> {
  children: ReactNode
  /** Force WebGL renderer (needed for raw GLSL ShaderMaterial) */
  forceWebGL?: boolean
}

/**
 * Drop-in <Canvas> replacement that initializes WebGPURenderer
 * with automatic WebGL2 fallback for unsupported browsers.
 *
 * Set forceWebGL=true for scenes that use raw GLSL ShaderMaterial
 * (incompatible with WebGPU's TSL pipeline).
 */
export function WebGPUCanvas({ children, forceWebGL, ...props }: WebGPUCanvasProps) {
  if (forceWebGL) {
    return (
      <Canvas
        gl={{ antialias: true, alpha: true }}
        {...props}
      >
        {children}
      </Canvas>
    )
  }

  return (
    <Canvas
      gl={async (props) => {
        const renderer = new THREE.WebGPURenderer(props as any)
        await renderer.init()
        return renderer
      }}
      {...props}
    >
      {children}
    </Canvas>
  )
}
